import React, { useCallback, useEffect, useState } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import { getStatusTextClass } from "../../../shared/utils/statusText";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  formatActivityValue,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

// ── Type Definitions: Retention policies, activity logs, and executions ──────

interface JobPolicyState {
  dailyBackup: number;
  weeklyBackup: number;
  monthlyBackup: number;
  yearlyBackup: number;
  isEnabled: boolean;
}

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  durationMs?: number | null;
  changesJson?: string;
  errorMessage?: string;
}

interface ExecutionEntry {
  id: string;
  executionId: string;
  actionType: string;
  executionType: string;
  triggerType: string;
  status: string;
  statusMessage?: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  durationMs?: number | null;
  errorMessage?: string;
  metadataJson?: string;
  pipelineRunUrl?: string;
}

// ── Utility Functions: Helper functions for activity matching and formatting ──

function isSameRetentionApplyEvent(execution: ExecutionEntry, audit: ActivityLogEntry): boolean {
  if ((execution.actionType || "").toLowerCase() !== "apply_settings") {
    return false;
  }

  const sameActor = (execution.actorName || "").trim().toLowerCase() === (audit.actorName || "").trim().toLowerCase();
  const executionTime = new Date(execution.startedAtUtc).getTime();
  const auditTime = new Date(audit.startedAtUtc).getTime();
  return sameActor && Math.abs(executionTime - auditTime) <= 5000;
}

function buildActivityFeed(executionItems: ExecutionEntry[], auditItems: ActivityLogEntry[]): ActivityFeedEntry[] {
  const executionFeed = mapExecutionFeedEntries(executionItems);

  const auditFeed: ActivityFeedEntry[] = auditItems
    .filter((log) => log.actionType === "save_retention_policy")
    .filter((log) => !executionItems.some((execution) => isSameRetentionApplyEvent(execution, log)))
    .map((log) => ({
      id: log.id,
      source: "audit" as const,
      actionType: log.actionType,
      status: log.status,
      actorName: log.actorName,
      actorEmail: log.actorEmail,
      startedAtUtc: log.startedAtUtc,
      updatedAtUtc: undefined,
      durationMs: log.durationMs,
      detailsJson: log.changesJson,
      errorMessage: log.errorMessage,
    }));

  return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
}

// ── Main Component: DbBackupRetention ───────────────────────────────────────────

const DbBackupRetention = () => {
  // ── State Management & Authentication
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();

  const [jobPolicy, setJobPolicy] = useState<JobPolicyState>({
    dailyBackup: 0,
    weeklyBackup: 0,
    monthlyBackup: 0,
    yearlyBackup: 0,
    isEnabled: false,
  });
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/xuno`;


  const fetchJobPolicy = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      const response = await axios.get(`${apiUrl}/jobs/policy`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setJobPolicy(response.data as JobPolicyState);
    } catch {
      toast.error("Failed to load DB backup retention settings.");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, setLoading]);

  // Xuno audit ChangesJson is { key: { before, after } }, not Prestia's fields array
  const extractAuditChangeLines = (changesJson?: string) => {
    const parsed = parseActivityDetailsJson<any>(changesJson);
    const labels: Record<string, string> = {
      dailyBackup: "Daily Backup",
      weeklyBackup: "Weekly Backup",
      monthlyBackup: "Monthly Backup",
      yearlyBackup: "Yearly Backup",
      isEnabled: "Policy Enabled",
    };

    return Object.entries(labels)
      .filter(([key]) => parsed?.[key] && hasActivityValueChanged(parsed[key].before, parsed[key].after))
      .map(([key, label]) => `${label}: ${formatActivityValue(parsed[key].before)} -> ${formatActivityValue(parsed[key].after)}`);
  };

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    if (log.source === "execution") {
      return [];
    }

    return extractAuditChangeLines(log.detailsJson);
  };

  const fetchActivityLogs = useCallback(async (page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${apiUrl}/jobs/policy/activity-logs`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionEntry>>(`${apiUrl}/jobs/policy/executions`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
      ]);
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl]);

  const updateJobPolicy = async (
    dailyAge: number,
    weeklyAge: number,
    monthlyAge: number,
    yearlyAge: number,
    isEnabled: boolean
  ) => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.put(
        `${apiUrl}/updatejobpolicy/`,
        {
          dailyBackup: dailyAge,
          weeklyBackup: weeklyAge,
          monthlyBackup: monthlyAge,
          yearlyBackup: yearlyAge,
          isEnabled,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      await fetchJobPolicy();
      void fetchActivityLogs(1, false, false);
      toast.success("Retention settings applied successfully.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to apply DB backup retention settings."));
    } finally {
      setLoading(false);
    }
  };

  const setProperty = (property: keyof JobPolicyState, value: number | boolean | string) => {
    setJobPolicy((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowApplyConfirm(true);
  };

  const handleConfirmApply = () => {
    setShowApplyConfirm(false);
    void updateJobPolicy(
      jobPolicy.dailyBackup,
      jobPolicy.weeklyBackup,
      jobPolicy.monthlyBackup,
      jobPolicy.yearlyBackup,
      jobPolicy.isEnabled
    );
  };

  // ── Effects: Data fetching on mount and polling for changes ───────────────
  useEffect(() => {
    if (!instance || accounts.length === 0) {
      return;
    }

    void fetchJobPolicy();
    void fetchActivityLogs(1);
  }, [accounts, fetchActivityLogs, fetchJobPolicy, instance]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions]);

  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: log.source === "execution"
      ? ((log.actionType || "").toLowerCase() === "run_cleanup"
        ? (log.triggerType === "manual_action" ? "Run Cleanup Now" : "Run Cleanup")
        : "Apply Settings")
      : "Apply Settings",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.source === "execution" ? log.pipelineRunUrl : undefined,
  }));

  // ── Render: Form UI and activity log display ──────────────────────────────
  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Sets how long Xuno MySQL backup files remain before cleanup removes them.">
          <h3 className="text-sm font-semibold text-slate-800">Cleanup policy</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Set daily, weekly, monthly, and yearly retention periods.</li>
            <li>Cleanup removes expired backup files from <span className="font-semibold">xunohosteddata</span>.</li>
            <li>Execution: Azure DevOps pipeline.</li>
            <li>Retention cleanup runs every Sunday at 5:00 AM AEST (6:00 AM AEDT).</li>
            <li>Set backup times on the DB Backup Schedule page.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && <div id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
      <div>
        <div className="workspace-card mx-auto mt-2 w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
          <h3 className="text-sm font-bold leading-5 text-slate-800">DB Backup Retention Settings</h3>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">All values below are retention periods in days.</p>
          <hr className="my-3 border-slate-200" />

          <div className="mb-3 grid grid-cols-1 gap-3 text-xs text-gray-700 md:grid-cols-2">
            <div>
              <span className="font-medium text-gray-600">Policy Status: </span>
              <span className={getStatusTextClass(jobPolicy.isEnabled ? "enabled" : "disabled")}>
                {jobPolicy.isEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} id="frmRetention">
            <div className="col-span-1 mb-3 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3 md:col-span-2">
              <div>
                <label htmlFor="isEnabled" className="block text-xs font-semibold text-gray-700">Enable Policy</label>
                <p className="mt-1 text-[11px] leading-4 text-slate-500">When enabled, old backups are deleted automatically based on the retention values below.</p>
              </div>
              <input
                type="checkbox"
                id="isEnabled"
                checked={jobPolicy.isEnabled}
                onChange={(e) => setProperty("isEnabled", e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-sm border border-slate-200 bg-white p-3">
                <label htmlFor="dailyBackup" className="block text-xs font-semibold leading-4 text-slate-700">Daily Backup</label>
                <p className="mt-1 mb-2 text-[11px] leading-4 text-slate-500">Retention period in days for all backups.</p>
                <input
                  type="number"
                  id="dailyBackup"
                  className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={jobPolicy.dailyBackup}
                  onChange={(e) => setProperty("dailyBackup", Number(e.target.value))}
                  min={0}
                />
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-3">
                <label htmlFor="weeklyBackup" className="block text-xs font-semibold leading-4 text-slate-700">Weekly Backup</label>
                <p className="mt-1 mb-2 text-[11px] leading-4 text-slate-500">Retention period in days for the Monday backup each week.</p>
                <input
                  type="number"
                  id="weeklyBackup"
                  className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={jobPolicy.weeklyBackup}
                  onChange={(e) => setProperty("weeklyBackup", Number(e.target.value))}
                  min={0}
                />
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-3">
                <label htmlFor="monthlyBackup" className="block text-xs font-semibold leading-4 text-slate-700">Monthly Backup</label>
                <p className="mt-1 mb-2 text-[11px] leading-4 text-slate-500">Retention period in days for the first Monday backup each month.</p>
                <input
                  type="number"
                  id="monthlyBackup"
                  className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={jobPolicy.monthlyBackup}
                  onChange={(e) => setProperty("monthlyBackup", Number(e.target.value))}
                  min={0}
                />
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-3">
                <label htmlFor="yearlyBackup" className="block text-xs font-semibold leading-4 text-slate-700">Yearly Backup</label>
                <p className="mt-1 mb-2 text-[11px] leading-4 text-slate-500">Retention period in days for the 1 January backup each year. Must be greater than Monthly Backup. Recommended: 3650 (10 years).</p>
                <input
                  type="number"
                  id="yearlyBackup"
                  className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={jobPolicy.yearlyBackup}
                  onChange={(e) => setProperty("yearlyBackup", Number(e.target.value))}
                  min={0}
                />
              </div>
            </div>
            <div className="mt-3 flex justify-start">
              <button type="submit" className={btn.applySmall}>Apply Settings</button>
            </div>
          </form>
        </div>
      </div>
      <ActivityLogTable
        title="Activity Log"
        description="Tracks retention changes and cleanup runs."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
      />
      </div>}

      <ConfirmActionDialog
        open={showApplyConfirm}
        title="Confirm DB Backup Retention"
        description="This can permanently delete backup data."
        confirmLabel="Yes, apply settings"
        destructive
        onConfirm={handleConfirmApply}
        onCancel={() => setShowApplyConfirm(false)}
      >
        <div className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
          <p className="font-medium text-gray-800">Settings to apply</p>
          <div className="mt-2 space-y-1 text-xs text-gray-600">
            <p>Daily Backup: <span className="font-bold text-blue-600">{jobPolicy.dailyBackup}</span> <span className="font-bold text-blue-600">days</span></p>
            <p>Weekly Backup: <span className="font-bold text-blue-600">{jobPolicy.weeklyBackup}</span> <span className="font-bold text-blue-600">days</span></p>
            <p>Monthly Backup: <span className="font-bold text-blue-600">{jobPolicy.monthlyBackup}</span> <span className="font-bold text-blue-600">days</span></p>
            <p>Yearly Backup: <span className="font-bold text-blue-600">{jobPolicy.yearlyBackup}</span> <span className="font-bold text-blue-600">days</span></p>
            <p>
              Policy Enabled:{" "}
              <span className={getStatusTextClass(jobPolicy.isEnabled ? "enabled" : "disabled")}>
                {jobPolicy.isEnabled ? "Enabled" : "Disabled"}
              </span>
            </p>
          </div>
        </div>
      </ConfirmActionDialog>
    </div>
  );
};

export default DbBackupRetention;
